import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { Icon } from '@matillion/component-library'

import { useDeleteSecretReference, useGetPermissions } from 'api/hooks'
import { GETSecretReferencesResponseParams } from 'api/types'
import EllipsesActionItem from 'components/EllipsesActionItem'
import ConfirmCancelModal from 'components/Modal/ProjectExplorerModal'
import { QueryKey } from 'constants/endpoint'
import { PermissionsType, SecretReferenceTypes } from 'types'

interface ActionCellProps {
  oauth: GETSecretReferencesResponseParams
  onDeleteHandler: (name: string, isSuccess?: boolean) => void
}

const ActionCell: FunctionComponent<ActionCellProps> = ({
  oauth: { id, name },
  onDeleteHandler
}) => {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync, isLoading } = useDeleteSecretReference(projectId!)

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const { data: deletePermission } = useGetPermissions(
    projectId!,
    PermissionsType.DeleteOAuths
  )

  const onSwitchModal = () => setIsDeleteModalVisible((v) => !v)

  const onDelete = () => {
    mutateAsync({ values: { id } })
      .then(() => {
        queryClient.invalidateQueries([
          QueryKey.SECRET_REFERENCES,
          projectId!,
          [
            SecretReferenceTypes.OAUTH_USER_TOKENS,
            SecretReferenceTypes.OAUTH_CLIENT_CRED,
            SecretReferenceTypes.COMPOSITE_TOKEN
          ]
        ])
        onSwitchModal()

        onDeleteHandler(name)
      })
      .catch(() => {
        onDeleteHandler(name, false)
      })
  }

  return deletePermission?.result ? (
    <>
      <EllipsesActionItem
        actions={[
          {
            title: t('secrets.oauthListing.actions.delete'),
            action: onSwitchModal,
            icon: <Icon.Trash />,
            type: 'danger',
            dataTestId: 'ellipses-action-delete-oauth'
          }
        ]}
      />

      {isDeleteModalVisible && (
        <ConfirmCancelModal
          onValidate={onDelete}
          onCancel={onSwitchModal}
          content={t('secrets.deleteOAuth.modal.content')}
          validate={t('secrets.deleteOAuth.modal.validate')}
          waiting={isLoading}
        />
      )}
    </>
  ) : null
}

export default ActionCell
